import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import confetti from "canvas-confetti";
import { Mic, MicOff } from "lucide-react";

const FIREWORKS_SFX =
  "https://customer-assets.emergentagent.com/job_swipe-to-love/artifacts/er5crj7y_YTMP3GG_YouTube_Birthday-Fireworks_Media_e5BVG7tYvjU_009_128k.mp3";

export default function CandleInteraction() {
  const [blown, setBlown] = useState(false);
  const [micActive, setMicActive] = useState(false);
  const [micError, setMicError] = useState(false);
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const rafRef = useRef(null);
  const streamRef = useRef(null);
  const fireworksAudioRef = useRef(null);

  const triggerBlowOut = () => {
    if (blown) return;
    setBlown(true);
    // Play fireworks SFX (overlays background music, doesn't stop it)
    try {
      const sfx = new Audio(FIREWORKS_SFX);
      sfx.volume = 0.85;
      fireworksAudioRef.current = sfx;
      const p = sfx.play();
      if (p && p.catch) p.catch(() => {});
    } catch (e) {}
    // Confetti burst!
    const colors = ["#D4AF37", "#FFE7A0", "#D71920", "#F8F5F0", "#FFB347"];
    confetti({
      particleCount: 80,
      spread: 70,
      origin: { y: 0.55 },
      colors,
    });
    setTimeout(() => {
      confetti({
        particleCount: 60,
        angle: 60,
        spread: 55,
        origin: { x: 0, y: 0.6 },
        colors,
      });
      confetti({
        particleCount: 60,
        angle: 120,
        spread: 55,
        origin: { x: 1, y: 0.6 },
        colors,
      });
    }, 250);
    // Additional firework-timed confetti bursts
    setTimeout(() => {
      confetti({ particleCount: 50, spread: 100, startVelocity: 55, origin: { x: 0.3, y: 0.4 }, colors });
    }, 900);
    setTimeout(() => {
      confetti({ particleCount: 50, spread: 100, startVelocity: 55, origin: { x: 0.7, y: 0.4 }, colors });
    }, 1500);
    // Stop mic
    stopMic();
  };

  const stopMic = () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (audioCtxRef.current && audioCtxRef.current.state !== "closed") {
      audioCtxRef.current.close();
      audioCtxRef.current = null;
    }
    setMicActive(false);
  };

  const startMic = async () => {
    setMicError(false);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      const ctx = new AudioCtx();
      audioCtxRef.current = ctx;
      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 1024;
      source.connect(analyser);
      analyserRef.current = analyser;
      const data = new Uint8Array(analyser.frequencyBinCount);
      setMicActive(true);

      const tick = () => {
        analyser.getByteTimeDomainData(data);
        // Compute volume (RMS-ish)
        let sum = 0;
        for (let i = 0; i < data.length; i++) {
          const v = (data[i] - 128) / 128;
          sum += v * v;
        }
        const rms = Math.sqrt(sum / data.length);
        // Threshold for "blow"
        if (rms > 0.22) {
          triggerBlowOut();
          return;
        }
        rafRef.current = requestAnimationFrame(tick);
      };
      tick();
    } catch (e) {
      setMicError(true);
      setMicActive(false);
    }
  };

  useEffect(() => {
    return () => stopMic();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCandleTap = () => {
    if (!blown) triggerBlowOut();
  };

  return (
    <div className="flex flex-col items-center" data-testid="candle-section">
      <div className="text-[10px] tracking-[0.4em] uppercase text-[#D4AF37]/80 mb-3 font-body">
        Make a wish
      </div>

      {/* Candle */}
      <button
        onClick={handleCandleTap}
        className="relative outline-none"
        style={{ background: "transparent", border: "none", cursor: "pointer" }}
        data-testid="candle-tap"
        aria-label="Tap to blow candle"
      >
        {/* Flame container */}
        <div className="relative h-16 flex items-end justify-center">
          <AnimatePresence>
            {!blown && (
              <motion.div
                key="flame"
                className="flame absolute bottom-1"
                initial={{ opacity: 0, scale: 0.6 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.2, y: -10 }}
                transition={{ duration: 0.3 }}
                style={{
                  width: 22,
                  height: 38,
                  boxShadow:
                    "0 0 30px rgba(255, 200, 80, 0.7), 0 0 60px rgba(255, 150, 50, 0.45)",
                }}
              />
            )}
            {blown && (
              <motion.div
                key="smoke"
                className="smoke absolute bottom-2"
                initial={{ opacity: 0.6, y: 0, scale: 0.6 }}
                animate={{ opacity: 0, y: -50, scale: 1.6 }}
                transition={{ duration: 1.6 }}
                style={{ width: 30, height: 40 }}
              />
            )}
          </AnimatePresence>
        </div>

        {/* Wick */}
        <div className="mx-auto w-[2px] h-2 bg-[#1A110D]" />

        {/* Candle body */}
        <div className="relative mx-auto" style={{ width: 32 }}>
          <div
            className="rounded-t-sm"
            style={{
              height: 80,
              background:
                "linear-gradient(180deg, #F8F5F0 0%, #E8DCC4 35%, #D4AF37 100%)",
              boxShadow:
                "inset -3px 0 6px rgba(0,0,0,0.18), inset 3px 0 6px rgba(255,255,255,0.6), 0 6px 16px rgba(0,0,0,0.3)",
              border: "1px solid rgba(212, 175, 55, 0.5)",
            }}
          />
          {/* Wax drip */}
          <div
            className="absolute top-1 left-0 right-0 mx-auto"
            style={{
              width: 3,
              height: 12,
              background: "#F8F5F0",
              borderRadius: 2,
              opacity: 0.7,
            }}
          />
        </div>
      </button>

      {/* Mic / Tap controls */}
      <div className="mt-5 flex flex-col items-center gap-2">
        {!blown && (
          <button
            onClick={micActive ? stopMic : startMic}
            data-testid="mic-toggle"
            className="flex items-center gap-2 px-4 py-2 rounded-full border border-[#D4AF37]/60 bg-[#1A110D]/40 text-[#F8F5F0] text-[11px] tracking-[0.25em] uppercase font-body backdrop-blur-md transition hover:bg-[#D4AF37]/20"
          >
            {micActive ? (
              <>
                <Mic className="w-3.5 h-3.5 text-[#D71920] animate-pulse" />
                Listening · blow!
              </>
            ) : (
              <>
                <MicOff className="w-3.5 h-3.5 text-[#D4AF37]" />
                Blow with your breath
              </>
            )}
          </button>
        )}
        {!blown && (
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#F8F5F0]/45 font-body">
            or tap the candle
          </div>
        )}
        {micError && !blown && (
          <div className="text-[10px] tracking-[0.2em] text-[#D71920]/80 font-body">
            mic blocked · tap the candle instead
          </div>
        )}
        {blown && (
          <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-[12px] tracking-[0.3em] uppercase font-body"
          >
            <span className="gold-shimmer font-medium">Wish made ·</span>{" "}
            <span className="text-[#F8F5F0]/80">happy birthday</span>
          </motion.div>
        )}
      </div>
    </div>
  );
}
